import React from 'react';
import CallToActionLinkAtom from '@atoms/link-atoms/call-to-action-link-atom';
import CircularExamplesSidebar from './circular-examples-sidebar/circular-examples-sidebar';
import styled from '@emotion/styled/macro';
import TileBlock from '../tile-block/tile-block';
import Translate from '@utils/translation-functions';
import vars from '@variables';
import { FeaturedCircularExampleBlockProps } from './featured-circular-example-block.types';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { H1Atom, H3Atom, PAtom } from '@atoms/text-atoms/text-atoms';

const FeaturedCircularExampleBlock: React.FC<
  FeaturedCircularExampleBlockProps
> = ({
  backgroundColour = '',
  gatsbyImageData = {},
  enableLightText = true,
  sideBarCircularExamples = [],
  featuredCircularExample,
  sidebarCta = { text: 'view all' },
  primaryCtaLinkText = 'read more',
  translate = true,
  className,
}) => {
  const gatsbyImage = getImage(gatsbyImageData);
  if (
    Array.isArray(featuredCircularExample) &&
    featuredCircularExample[0]?.renderType !== 'runtime'
  ) {
    const featuredCEMobileTiles = [
      featuredCircularExample[0],
      ...sideBarCircularExamples,
    ];
    return (
      <div data-testid="fce-block" className={className}>
        <div className="content-container">
          <H3Atom className="temporary-block-title">
            {Translate('Circular economy examples', translate)}
          </H3Atom>
          <H1Atom data-testid="fce-block-title">
            <span>{featuredCircularExample[0].title}</span>
          </H1Atom>
          <PAtom data-testid="fce-block-desc">
            {featuredCircularExample[0].description}
          </PAtom>
          <CallToActionLinkAtom
            url={featuredCircularExample[0].url}
            buttonText={primaryCtaLinkText}
            data-gtm-id={GTM_ID.READ_MORE}
          />
        </div>
        <CircularExamplesSidebar
          tiles={sideBarCircularExamples}
          sidebarCta={sidebarCta}
          data-testid="fce-sidebar"
        />
        <TileBlock
          colour="white"
          title={Translate('Circular economy examples', translate)}
          tiles={featuredCEMobileTiles}
          isCarousel
          translate={translate}
          className={className}
        />
        <div
          className="fce-background-image"
          style={{
            backgroundColor: backgroundColour,
          }}
        >
          {gatsbyImageData && gatsbyImage && (
            <GatsbyImage
              image={gatsbyImage}
              alt={Translate('Circular economy examples', translate)}
              className="gatsby-image-pos"
            />
          )}
        </div>
      </div>
    );
  } else return null;
};

const StyledFeaturedCircularExampleBlock = styled(FeaturedCircularExampleBlock)`
  width: 100%;
  max-width: ${vars.contentMaxWidth};

  display: flex;
  justify-content: space-between;
  background-size: cover;
  background-repeat: no-repeat;
  padding-top: 3.4rem;
  @media screen and (min-width: ${vars.breakPointTwelveColumn}) {
    padding-top: 9.6rem;
    height: 54rem;
  }

  * {
    z-index: 30;
  }

  .temporary-block-title {
    font-weight: ${vars.fontWeightRegular};
    padding: 0;
    margin: -4rem 0 3.5rem 0;
    color: white;
  }

  .content-container {
    ${H1Atom} {
      margin: -0.6rem 0 ${vars.spacingL} ${vars.spacingS};
      width: 64.1rem;
      line-height: 1.4;
      span {
        font-weight: ${vars.fontWeightBold};
        background-color: white;
        box-shadow:
          ${vars.spacingS} 0 0 white,
          -${vars.spacingS} 0 0 white;
        font-size: 3.8rem;
        letter-spacing: -0.02rem;
        padding-bottom: 4px;
      }
    }

    ${PAtom} {
      color: ${({ enableLightText }) =>
        enableLightText ? vars.white : vars.emfBlue};
      width: 39rem;
      margin-bottom: ${vars.spacingL};
      font-weight: ${vars.fontWeightRegular};
      ${vars.fontSize5};
      letter-spacing: -0.02em;
      line-height: 1.5;
    }
  }

  .fce-background-image {
    position: absolute;
    z-index: 20;
    left: 0;
    width: 100%;
    height: 33.2rem;
    .gatsby-image-pos {
      position: absolute;
      height: 100%;
      transition: ${vars.defaultTransition};
      img {
        display: none;
        transition: ${vars.defaultTransition};
        position: absolute;
      }
    }
    @media screen and (min-width: ${vars.breakPointTwelveColumn}) {
      height: 54rem;
      margin-top: -9.6rem;
      .gatsby-image-pos img {
        display: block;
      }
    }
  }

  /* Responsive behaviour below */

  ${CircularExamplesSidebar}, .content-container {
    display: none;
    @media screen and (min-width: ${vars.breakPointTwelveColumn}) {
      display: block;
    }
  }
`;

export default StyledFeaturedCircularExampleBlock;
